import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SchedulePrintView from './SchedulePrintView';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Types ────────────────────────────────────────────────────────────────────

interface BowlerEntry {
    CLAVE: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    EMAIL: string;
    TELEFONO: string | null;
    CITY: string | null;
    CVEESTADO: string | null;
    DIVISION_CLAVE: number | null;
    DIVISION_NOMBRE: string | null;
    RECRUITER_NAME: string | null;
}

interface EntryPackage {
    CVEPAQUETE: number;
    DESCRIPCION: string;
    COSTO: number;
    PACKAGETYPE: string;
}

interface Balance {
    COMPRADO: number;
    PAGADO: number;
    BALANCE: number;
}

interface TournamentEvent {
    CLAVE: number;
    NOMBRE: string;
    REGISTERED: number;
    ALLOWED: number;
}

interface Squad {
    CLAVE: number;
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    DIFERENCIA: number;
    WLINSCRITOS: number;
    CVEEVENTO: number;
    IS_REGISTERED: boolean;
    JUGADO: number | null;
}

interface AvailablePackage {
    CLAVE: number;
    DESCRIPCION: string;
    COSTO: number;
    PACKAGETYPE: string;
}

interface PackageEvent {
    CVEEVENTO: number;
    CANTIDAD: number;
    EVENTO: string;
}

interface Payment {
    CLAVE: number;
    FECHA: string;
    CANTIDAD: number;
    CVEFORMAPAGO: number;
    CVEVENDEDOR: number;
}

interface RegisteredSquad {
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    CVEEVENTO: number;
    EVENTO_NOMBRE: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const paymentTypeLabel = (id: number) => {
    const map: Record<number, string> = { 1: 'Cash', 2: 'Check', 3: 'Card / Clover', 4: 'Card / Stripe' };
    return map[id] || `Type ${id}`;
};

const pkgTypeLabel = (t: string) => {
    const map: Record<string, string> = { '1': 'Main', '2': 'Re-Entry', '3': 'Side Event' };
    return map[t] || 'Package';
};

const pkgTypeBadge = (t: string) => {
    if (t === '1') return 'bg-blue-100 text-blue-700';
    if (t === '2') return 'bg-green-100 text-green-700';
    if (t === '3') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
};

// ─── Component ────────────────────────────────────────────────────────────────

const AdminEntryPOS: React.FC = () => {
    const { torneoClave, jugadorClave } = useParams<{ torneoClave: string; jugadorClave: string }>();
    const navigate = useNavigate();

    // Auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    const adminData = JSON.parse(localStorage.getItem('loggedInAdmin') || '{}');
    const adminVendedorClave = adminData?.CLAVE || -1;

    // ── Core data ──────────────────────────────────────────────────────
    const [bowler, setBowler] = useState<BowlerEntry | null>(null);
    const [packages, setPackages] = useState<EntryPackage[]>([]);
    const [balance, setBalance] = useState<Balance>({ COMPRADO: 0, PAGADO: 0, BALANCE: 0 });
    const [events, setEvents] = useState<TournamentEvent[]>([]);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ── Event tabs & squads ────────────────────────────────────────────
    const [activeEventId, setActiveEventId] = useState<number | null>(null);
    const [squads, setSquads] = useState<Squad[]>([]);
    const [squadsLoading, setSquadsLoading] = useState(false);

    // ── Move squad (inline) ────────────────────────────────────────────
    const [movingSquadId, setMovingSquadId] = useState<number | null>(null);
    const [selectedMoveTarget, setSelectedMoveTarget] = useState<number | null>(null);
    const [moveWaitlist, setMoveWaitlist] = useState(false);
    const [moveSaving, setMoveSaving] = useState(false);
    const [moveError, setMoveError] = useState('');

    // ── Add package (inline) ─────────────────────────────────────────
    const [addPkgMode, setAddPkgMode] = useState(false);
    const [availablePkgs, setAvailablePkgs] = useState<AvailablePackage[]>([]);
    const [selectedPkgToAdd, setSelectedPkgToAdd] = useState<number | null>(null);
    const [addPkgSaving, setAddPkgSaving] = useState(false);
    const [addPkgError, setAddPkgError] = useState('');

    // ── Remove package ─────────────────────────────────────────────────
    const [removingPkg, setRemovingPkg] = useState<EntryPackage | null>(null);
    const [removeConflicts, setRemoveConflicts] = useState<string[]>([]);
    const [removeError, setRemoveError] = useState('');
    const [removeSaving, setRemoveSaving] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    // ── Payment modal ──────────────────────────────────────────────────
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentType, setPaymentType] = useState<'cash' | 'card' | 'check'>('cash');
    const [paymentSaving, setPaymentSaving] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [showPaymentsHistory, setShowPaymentsHistory] = useState(false);

    // ── Load entry data ────────────────────────────────────────────────
    const loadEntry = useCallback(async () => {
        if (!torneoClave || !jugadorClave) return;
        setLoading(true);
        setError('');
        try {
            const [entryRes, eventsRes, paymentsRes] = await Promise.all([
                fetch(`${BACKEND_URL}/api/admin/pos/entry/${torneoClave}/${jugadorClave}`),
                fetch(`${BACKEND_URL}/api/admin/pos/tournament-events/${torneoClave}/${jugadorClave}`),
                fetch(`${BACKEND_URL}/api/admin/pos/payments/${torneoClave}/${jugadorClave}`),
            ]);
            const entryData = await entryRes.json();
            const eventsData = await eventsRes.json();
            const paymentsData = await paymentsRes.json();

            if (!entryRes.ok) throw new Error(entryData.error || 'Failed to load entry');

            setBowler(entryData.bowler);
            setPackages(entryData.packages || []);
            setBalance(entryData.balance || { COMPRADO: 0, PAGADO: 0, BALANCE: 0 });
            setEvents(eventsData || []);
            setPayments(paymentsData || []);

            setActiveEventId(prev => prev !== null ? prev : (eventsData?.[0]?.CLAVE ?? null));
        } catch (e: any) {
            setError(e.message || 'Failed to load entry');
        } finally {
            setLoading(false);
        }
    }, [torneoClave, jugadorClave]);

    useEffect(() => { loadEntry(); }, [loadEntry]);

    // ── Load squads when event tab changes ─────────────────────────────
    const loadSquads = useCallback(async (eventId: number) => {
        if (!torneoClave || !jugadorClave) return;
        setSquadsLoading(true);
        try {
            const divisionClave = bowler?.DIVISION_CLAVE || 0;
            const res = await fetch(
                `${BACKEND_URL}/api/admin/pos/event-squads/${torneoClave}/${eventId}/${jugadorClave}/${divisionClave}`
            );
            const data = await res.json();
            setSquads(data || []);
        } catch {
            setSquads([]);
        } finally {
            setSquadsLoading(false);
        }
    }, [torneoClave, jugadorClave, bowler]);

    useEffect(() => {
        if (activeEventId !== null) loadSquads(activeEventId);
    }, [activeEventId, loadSquads]);

    // ── Move squad (inline) ────────────────────────────────────────────
    const startMove = (squad: Squad) => {
        setMovingSquadId(squad.CLAVE);
        setSelectedMoveTarget(null);
        setMoveWaitlist(false);
        setMoveError('');
    };

    const cancelMove = () => {
        setMovingSquadId(null);
        setSelectedMoveTarget(null);
        setMoveWaitlist(false);
        setMoveError('');
    };

    const handleMoveSquad = async (squad: Squad) => {
        if (!selectedMoveTarget || !torneoClave || !jugadorClave) return;
        setMoveSaving(true);
        setMoveError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/move-squad`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    oldSquadId: squad.CLAVE,
                    newSquadId: selectedMoveTarget,
                    waitlist: moveWaitlist,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to move squad');
            cancelMove();
            if (activeEventId !== null) loadSquads(activeEventId);
        } catch (e: any) {
            setMoveError(e.message);
        } finally {
            setMoveSaving(false);
        }
    };

    // ── Add package (inline) ─────────────────────────────────────────
    const cancelAddPkg = () => {
        setAddPkgMode(false);
        setSelectedPkgToAdd(null);
        setAddPkgError('');
    };

    const startAddPkg = async () => {
        setAddPkgMode(true);
        setSelectedPkgToAdd(null);
        setAddPkgError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/available-packages/${torneoClave}`);
            const data = await res.json();
            setAvailablePkgs(data || []);
        } catch { setAvailablePkgs([]); }
    };

    const handleAddPackage = async () => {
        if (!selectedPkgToAdd || !torneoClave || !jugadorClave) return;
        setAddPkgSaving(true);
        setAddPkgError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/add-package`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    paqueteClave: selectedPkgToAdd,
                    squadSelections: [],
                    vendedorClave: adminVendedorClave,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add package');
            cancelAddPkg();
            await loadEntry();
            if (activeEventId !== null) loadSquads(activeEventId);
        } catch (e: any) {
            setAddPkgError(e.message);
        } finally {
            setAddPkgSaving(false);
        }
    };

    // ── Add squad directly from the tab grid ──────────────────────────
    const handleAddSquad = async (squad: Squad, waitlist = false) => {
        if (!torneoClave || !jugadorClave) return;
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/add-squad`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    turnoId: squad.CLAVE,
                    waitlist,
                }),
            });
            const data = await res.json();
            if (!res.ok) { alert(data.error || 'Failed to add squad'); return; }
            if (activeEventId !== null) loadSquads(activeEventId);
            await loadEntry();
        } catch (e: any) { alert(e.message); }
    };

    // ── Remove package ─────────────────────────────────────────────────
    const openRemovePkg = (pkg: EntryPackage) => {
        setRemovingPkg(pkg);
        setRemoveConflicts([]);
        setRemoveError('');
        setShowRemoveConfirm(true);
    };

    const handleRemovePackage = async () => {
        if (!removingPkg || !torneoClave || !jugadorClave) return;
        setRemoveSaving(true);
        setRemoveError('');
        setRemoveConflicts([]);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/remove-package`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    paqueteClave: removingPkg.CVEPAQUETE,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setRemoveError(data.error || 'Failed to remove package');
                setRemoveConflicts(data.conflicts || []);
                return;
            }
            setShowRemoveConfirm(false);
            setRemovingPkg(null);
            await loadEntry();
            if (activeEventId !== null) loadSquads(activeEventId);
        } catch (e: any) {
            setRemoveError(e.message);
        } finally {
            setRemoveSaving(false);
        }
    };

    // ── Remove squad ───────────────────────────────────────────────────
    const handleRemoveSquad = async (squad: Squad) => {
        if (!torneoClave || !jugadorClave) return;
        if (!window.confirm(`Remove squad #${squad.TURNO} (${squad.DIASEMANA_I} ${squad.HORARIO_I}) from this bowler?`)) return;
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/remove-squad`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    turnoId: squad.CLAVE,
                }),
            });
            const data = await res.json();
            if (!res.ok) { alert(data.error || 'Failed to remove squad'); return; }
            if (activeEventId !== null) loadSquads(activeEventId);
            await loadEntry(); // refresh event counters
        } catch (e: any) { alert(e.message); }
    };
    const [showPrint, setShowPrint] = useState(false);
    const [printSquads, setPrintSquads] = useState<RegisteredSquad[]>([]);
    const [printLoading, setPrintLoading] = useState(false);

    const handlePrint = async () => {
        if (!torneoClave || !jugadorClave || !bowler) return;
        setPrintLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/registered-squads/${torneoClave}/${jugadorClave}`);
            const data = await res.json();
            setPrintSquads(data || []);
            setShowPrint(true);
        } catch {
            setPrintSquads([]);
            setShowPrint(true);
        } finally {
            setPrintLoading(false);
        }
    };

    // ── Log payment ────────────────────────────────────────────────────
    const handleLogPayment = async () => {
        const amt = Number(paymentAmount);
        if (!amt || amt <= 0) { setPaymentError('Enter a valid amount'); return; }
        if (!torneoClave || !jugadorClave) return;
        setPaymentSaving(true);
        setPaymentError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/pos/log-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: Number(torneoClave),
                    jugadorClave: Number(jugadorClave),
                    amount: amt,
                    paymentType,
                    vendedorClave: adminVendedorClave,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to log payment');
            setShowPaymentModal(false);
            setPaymentAmount('');
            setPaymentType('cash');
            await loadEntry(); // refresh balance + payments
            setShowPaymentsHistory(true);
        } catch (e: any) {
            setPaymentError(e.message);
        } finally {
            setPaymentSaving(false);
        }
    };

    // ─── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100 flex items-center justify-center">
                <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-600" />
            </div>
        );
    }

    if (error || !bowler) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Failed to Load Entry</h2>
                    <p className="text-gray-500 text-sm mb-6">{error}</p>
                    <button onClick={() => navigate(-1)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold transition">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const registeredSquadCount = squads.filter(s => s.IS_REGISTERED).length;
    const activeEvent = events.find(e => e.CLAVE === activeEventId);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100 pb-28">

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="text-gray-500 hover:text-gray-800 transition p-2 rounded-lg hover:bg-gray-100"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl font-bold text-gray-900">POS — Entry</h1>
                                    <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-0.5 rounded-full">Admin</span>
                                </div>
                                <p className="text-sm text-gray-500 leading-tight">{bowler.NOMBRECOMPLETO} · TAT #{bowler.FICHANAL}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowPaymentModal(true)}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition shadow-lg"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Capture Payment
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={printLoading}
                            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-60"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            {printLoading ? 'Loading…' : 'Print Schedule'}
                        </button>
                        {showPrint && (
                            <SchedulePrintView
                                bowler={bowler}
                                packages={packages}
                                payments={payments}
                                balance={balance}
                                registeredSquads={printSquads}
                                onClose={() => setShowPrint(false)}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

                {/* ── Top row: Bowler info + Balance ─────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                    {/* Bowler Info */}
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-md p-6">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Bowler</h2>
                        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
                                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                                <div>
                                    <p className="text-lg font-bold text-gray-900 leading-tight">{bowler.NOMBRECOMPLETO}</p>
                                    <p className="text-sm text-indigo-600 font-semibold">TAT #{bowler.FICHANAL}</p>
                                </div>
                                <div className="text-sm text-gray-600 space-y-1">
                                    <p>{bowler.EMAIL}</p>
                                    {bowler.TELEFONO && <p>{bowler.TELEFONO}</p>}
                                    {bowler.CITY && <p>{bowler.CITY}{bowler.CVEESTADO ? `, ${bowler.CVEESTADO}` : ''}</p>}
                                </div>
                                {bowler.DIVISION_NOMBRE && (
                                    <div className="sm:col-span-2">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                                            Division: {bowler.DIVISION_NOMBRE}
                                        </span>
                                    </div>
                                )}
                                {bowler.RECRUITER_NAME && (
                                    <div className="sm:col-span-2 text-xs text-gray-500">
                                        Recruiter: <span className="font-semibold text-indigo-600">{bowler.RECRUITER_NAME}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Balance */}
                    <div className="bg-white rounded-2xl shadow-md p-6">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Balance</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Total Bought</span>
                                <span className="font-semibold text-gray-800">{fmt(Number(balance.COMPRADO))}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Total Paid</span>
                                <span className="font-semibold text-green-700">{fmt(Number(balance.PAGADO))}</span>
                            </div>
                            <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
                                <span className="text-sm font-bold text-gray-700">Balance Due</span>
                                <span className={`text-lg font-bold ${Number(balance.BALANCE) > 0 ? 'text-red-600' : Number(balance.BALANCE) < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                    {fmt(Number(balance.BALANCE))}
                                </span>
                            </div>
                            {Number(balance.BALANCE) > 0 && (
                                <button
                                    onClick={() => setShowPaymentModal(true)}
                                    className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2.5 rounded-xl transition"
                                >
                                    Collect Payment
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Packages ────────────────────────────────────────────────── */}
                <div className="bg-white rounded-2xl shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Packages ({packages.length})</h2>
                        {addPkgMode ? (
                            <button
                                onClick={cancelAddPkg}
                                className="text-gray-500 hover:text-gray-700 text-xs font-semibold px-3.5 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition"
                            >
                                ✕ Cancel
                            </button>
                        ) : (
                            <button
                                onClick={startAddPkg}
                                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add Package
                            </button>
                        )}
                    </div>
                    {addPkgMode && (
                        <div className="mb-5 space-y-2">
                            <div className="flex items-center gap-3 bg-indigo-50 border-2 border-indigo-200 rounded-xl px-4 py-3">
                                <svg className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <select
                                    className="flex-1 bg-transparent text-sm font-semibold text-indigo-900 focus:outline-none cursor-pointer"
                                    value={selectedPkgToAdd || ''}
                                    onChange={e => setSelectedPkgToAdd(Number(e.target.value) || null)}
                                >
                                    <option value="">— Select a package to add —</option>
                                    {availablePkgs.map(p => (
                                        <option key={p.CLAVE} value={p.CLAVE}>
                                            [{pkgTypeLabel(p.PACKAGETYPE)}] {p.DESCRIPCION} — {fmt(p.COSTO)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {addPkgError && <p className="text-red-600 text-xs font-medium px-1">{addPkgError}</p>}
                            <div className="flex gap-2">
                                <button
                                    onClick={cancelAddPkg}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold py-2 rounded-xl transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddPackage}
                                    disabled={!selectedPkgToAdd || addPkgSaving}
                                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold py-2 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {addPkgSaving ? 'Adding…' : 'Confirm Add Package'}
                                </button>
                            </div>
                        </div>
                    )}
                    {packages.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No packages purchased.</p>
                    ) : (
                        <div className="space-y-2">
                            {packages.map((pkg, i) => (
                                <div key={i} className="flex items-center justify-between gap-4 bg-gray-50 rounded-xl px-4 py-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${pkgTypeBadge(pkg.PACKAGETYPE)}`}>
                                            {pkgTypeLabel(pkg.PACKAGETYPE)}
                                        </span>
                                        <span className="text-sm font-medium text-gray-800 truncate">{pkg.DESCRIPCION}</span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <span className="text-sm font-semibold text-gray-700">{fmt(pkg.COSTO)}</span>
                                        <button
                                            onClick={() => openRemovePkg(pkg)}
                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition"
                                            title="Remove package"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <div className="flex justify-end pt-1">
                                <span className="text-sm font-bold text-gray-600">
                                    Total: {fmt(packages.reduce((s, p) => s + Number(p.COSTO), 0))}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Payment History ──────────────────────────────────────────── */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <button
                        onClick={() => setShowPaymentsHistory(v => !v)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition"
                    >
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            Payment History ({payments.length})
                        </h2>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${showPaymentsHistory ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {showPaymentsHistory && (
                        <div className="px-6 pb-5">
                            {payments.length === 0 ? (
                                <p className="text-sm text-gray-400 italic">No payments recorded.</p>
                            ) : (
                                <div className="space-y-1.5">
                                    {payments.map((p, i) => (
                                        <div key={i} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-gray-100 last:border-0">
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.CVEFORMAPAGO === 1 ? 'bg-green-100 text-green-700' : p.CVEFORMAPAGO === 3 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {paymentTypeLabel(p.CVEFORMAPAGO)}
                                                </span>
                                                <span className="text-gray-500 text-xs">{p.FECHA ? new Date(p.FECHA).toLocaleDateString() : '—'}</span>
                                            </div>
                                            <span className="font-semibold text-green-700">{fmt(Number(p.CANTIDAD))}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Event Tabs & Squads ──────────────────────────────────────── */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <div className="px-6 pt-5 pb-0">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Schedule — All Tournament Events</h2>
                        {/* Tabs */}
                        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-0">
                            {events.map(ev => {
                                const isActive = ev.CLAVE === activeEventId;
                                const scheduled = ev.REGISTERED;
                                const allowed = ev.ALLOWED;
                                const isFull = allowed > 0 && scheduled >= allowed;
                                const isEmpty = allowed === 0;
                                return (
                                    <button
                                        key={ev.CLAVE}
                                        onClick={() => setActiveEventId(ev.CLAVE)}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition -mb-px border-b-2 ${isActive
                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
                                            }`}
                                    >
                                        <span>{ev.NOMBRE}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${isEmpty ? 'bg-gray-200 text-gray-400' :
                                                isFull ? 'bg-green-500 text-white' :
                                                    'bg-yellow-400 text-white'
                                            }`}>
                                            {isEmpty ? '—' : `${scheduled}/${allowed}`}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Squad Grid */}
                    <div className="p-6">
                        {!activeEventId ? (
                            <p className="text-gray-400 text-sm italic">Select an event tab.</p>
                        ) : squadsLoading ? (
                            <div className="flex justify-center py-10">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                            </div>
                        ) : squads.length === 0 ? (
                            <p className="text-gray-400 text-sm italic">No squads found for this event.</p>
                        ) : (
                            <>
                                {activeEvent && activeEvent.ALLOWED > 0 && (
                                    <div className="flex items-center gap-3 mb-4 text-sm">
                                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">
                                            Package allows {activeEvent.ALLOWED} squad(s)
                                        </span>
                                        <span className={`px-3 py-1 rounded-full font-bold ${registeredSquadCount >= activeEvent.ALLOWED ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {registeredSquadCount} scheduled
                                        </span>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                    {squads.map(squad => {
                                        const full = squad.DIFERENCIA <= 0 && !squad.IS_REGISTERED;
                                        const bowled = squad.JUGADO === 2;
                                        return (
                                            <div
                                                key={squad.CLAVE}
                                                className={`rounded-xl border-2 p-4 transition ${squad.IS_REGISTERED
                                                        ? bowled
                                                            ? 'bg-gray-100 border-gray-300'
                                                            : 'bg-green-50 border-green-400'
                                                        : full
                                                            ? 'bg-red-50 border-red-200 opacity-70'
                                                            : 'bg-white border-gray-200 hover:border-indigo-300'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div>
                                                        <p className="font-bold text-gray-800 text-sm">Squad #{squad.TURNO}</p>
                                                        <p className="text-xs text-gray-500">{squad.DIASEMANA_I}</p>
                                                        <p className="text-xs text-gray-500">{squad.HORARIO_I}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        {squad.IS_REGISTERED ? (
                                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${bowled ? 'bg-gray-400 text-white' : 'bg-green-500 text-white'}`}>
                                                                {bowled ? 'BOWLED' : '✓ REG'}
                                                            </span>
                                                        ) : full ? (
                                                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">FULL</span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 font-medium">{squad.DIFERENCIA} left</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {squad.IS_REGISTERED && !bowled && (
                                                    movingSquadId === squad.CLAVE ? (
                                                        <div className="mt-3 space-y-2">
                                                            <select
                                                                value={selectedMoveTarget || ''}
                                                                onChange={e => { setSelectedMoveTarget(Number(e.target.value) || null); setMoveWaitlist(false); }}
                                                                className="w-full text-xs border border-gray-200 rounded-lg py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                                                            >
                                                                <option value="">— Move to which squad? —</option>
                                                                {squads.filter(s => !s.IS_REGISTERED && s.CLAVE !== squad.CLAVE).map(s => (
                                                                    <option key={s.CLAVE} value={s.CLAVE}>
                                                                        #{s.TURNO} {s.DIASEMANA_I} {s.HORARIO_I} {s.DIFERENCIA <= 0 ? '(FULL)' : `(${s.DIFERENCIA} left)`}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {selectedMoveTarget && (squads.find(s => s.CLAVE === selectedMoveTarget)?.DIFERENCIA ?? 1) <= 0 && (
                                                                <label className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 cursor-pointer">
                                                                    <input type="checkbox" checked={moveWaitlist} onChange={e => setMoveWaitlist(e.target.checked)} className="rounded" />
                                                                    Waitlist (squad is full)
                                                                </label>
                                                            )}
                                                            {moveError && <p className="text-red-600 text-xs font-medium">{moveError}</p>}
                                                            <div className="flex gap-1.5">
                                                                <button onClick={cancelMove} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold py-1.5 rounded-lg transition">
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() => handleMoveSquad(squad)}
                                                                    disabled={!selectedMoveTarget || moveSaving || ((squads.find(s => s.CLAVE === selectedMoveTarget)?.DIFERENCIA ?? 1) <= 0 && !moveWaitlist)}
                                                                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold py-1.5 rounded-lg transition disabled:opacity-50"
                                                                >
                                                                    {moveSaving ? 'Moving…' : 'Confirm'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2 mt-3">
                                                            <button
                                                                onClick={() => startMove(squad)}
                                                                className="flex-1 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white py-1.5 rounded-lg transition"
                                                            >
                                                                Move
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveSquad(squad)}
                                                                className="text-xs font-semibold text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-lg transition border border-red-200"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )
                                                )}
                                                {!squad.IS_REGISTERED && activeEvent && activeEvent.ALLOWED > 0 && registeredSquadCount < activeEvent.ALLOWED && (
                                                    <div className="mt-3">
                                                        <button
                                                            onClick={() => handleAddSquad(squad, full)}
                                                            className={`w-full text-xs font-semibold py-1.5 rounded-lg transition ${full
                                                                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'
                                                                    : 'bg-green-600 hover:bg-green-700 text-white'
                                                                }`}
                                                        >
                                                            {full ? 'Add to Waitlist' : '+ Add to Schedule'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>

            </div>



            {/* ── Remove Package Confirm Modal ──────────────────────────────────── */}
            {showRemoveConfirm && removingPkg && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">Remove Package?</h3>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Remove <span className="font-semibold text-gray-800">{removingPkg.DESCRIPCION}</span> ({fmt(removingPkg.COSTO)}) from this bowler's registration?
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 mb-4">
                                <strong>Note:</strong> This will only succeed if the bowler's registered squads are still covered by remaining packages. If they aren't, you must remove extra squads first or add a replacement package.
                            </div>
                            {removeConflicts.length > 0 && (
                                <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-4">
                                    <p className="text-red-700 font-bold text-sm mb-2">Cannot remove — constraint violations:</p>
                                    {removeConflicts.map((c, i) => (
                                        <p key={i} className="text-red-600 text-xs">• {c}</p>
                                    ))}
                                </div>
                            )}
                            {removeError && !removeConflicts.length && (
                                <p className="text-red-600 text-sm font-medium mb-3">{removeError}</p>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => { setShowRemoveConfirm(false); setRemovingPkg(null); setRemoveConflicts([]); setRemoveError(''); }}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemovePackage}
                                disabled={removeSaving}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50"
                            >
                                {removeSaving ? 'Removing…' : 'Remove Package'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Capture Payment Modal ─────────────────────────────────────────── */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">Capture Payment</h3>
                            <p className="text-sm text-gray-500 mt-0.5">Log an in-person payment. No processing — record only.</p>
                        </div>
                        <div className="p-6 space-y-4">
                            {/* Current balance info */}
                            <div className="bg-gray-50 rounded-xl p-3 flex justify-between text-sm">
                                <span className="text-gray-500">Balance due</span>
                                <span className={`font-bold ${Number(balance.BALANCE) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                    {fmt(Number(balance.BALANCE))}
                                </span>
                            </div>
                            {/* Amount */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Amount</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={paymentAmount}
                                        onChange={e => setPaymentAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full border-2 border-gray-200 rounded-xl pl-8 pr-4 py-3 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        autoFocus
                                    />
                                </div>
                                {Number(balance.BALANCE) > 0 && (
                                    <button
                                        onClick={() => setPaymentAmount(String(Number(balance.BALANCE).toFixed(2)))}
                                        className="mt-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
                                    >
                                        Use balance amount ({fmt(Number(balance.BALANCE))})
                                    </button>
                                )}
                            </div>
                            {/* Payment type */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Payment Method</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {(['cash', 'card', 'check'] as const).map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setPaymentType(type)}
                                            className={`py-2.5 rounded-xl font-semibold text-sm capitalize border-2 transition ${paymentType === type
                                                    ? 'border-green-500 bg-green-50 text-green-700'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                                }`}
                                        >
                                            {type === 'cash' ? '💵 Cash' : type === 'card' ? '💳 Card' : '📝 Check'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {paymentType === 'card' && Number(paymentAmount) > 0 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs space-y-1">
                                    <div className="flex justify-between text-blue-700">
                                        <span>5% card fee:</span>
                                        <span className="font-semibold">{fmt(+(Number(paymentAmount) * 0.05).toFixed(2))}</span>
                                    </div>
                                    <div className="flex justify-between text-blue-900 font-bold">
                                        <span>Charge on terminal:</span>
                                        <span>{fmt(+(Number(paymentAmount) * 1.05).toFixed(2))}</span>
                                    </div>
                                </div>
                            )}
                            {paymentError && <p className="text-red-600 text-sm font-medium">{paymentError}</p>}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentError(''); }}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLogPayment}
                                disabled={!paymentAmount || Number(paymentAmount) <= 0 || paymentSaving}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {paymentSaving ? 'Saving…' : 'Record Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEntryPOS;
